<script setup lang="ts">
import {inject, ref} from "vue";
import {RouterLink, useRouter} from "vue-router";
import {useConfirm} from "primevue/useconfirm";
import {deleteGroup, getGroup as apiGetGroup} from "@/Services/Admin/admin-groups";
import {removeGroupMember} from "@/Services/Admin/admin-group-members";
import Str from "@/Utils/Str";
import {PageHeading, PageLoading} from "@/Components/Global";
import {Avatar, Button, Card, Chip, Icon, Menu, Tag} from "@/Components/Library";
import type dayjs from "dayjs";
import {useToast} from "primevue/usetoast";

const $date = inject("date") as typeof dayjs;
const toast = useToast();
const router = useRouter();
const confirm = useConfirm();

const group = ref();
const groupMenuContainer = ref<Menu>();
const groupMenuItems = [
    {
        label: "Edit",
        icon: "prime:pencil",
        command: () => {
            router.push({
                name: "admin.groups.edit",
                params: {group: router.currentRoute.value.params.group},
            });
        },
    },
    {
        label: "Archive",
        icon: "prime:trash",
        class: "text-red-600 hover:bg-red-100",
        command: () => archiveGroup(),
    },
];

const getGroup = async () => {
    apiGetGroup(router.currentRoute.value.params.group).then(({data}) => (group.value = data));
};
getGroup();

const archiveGroup = () => {
    confirm.require({
        message: "Are you sure you want to archive this group?",
        accept: async () => {
            await deleteGroup(router.currentRoute.value.params.group);
            await router.push({name: "admin.groups.index"});
        },
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
    });
};

const archiveMember = (memberId: string) =>
    confirm.require({
        message: "Are you sure you want to archive this member?",
        accept: async () => {
            await removeGroupMember(router.currentRoute.value.params.group, memberId);
            toast.add({
                severity: "success",
                summary: "Member archived",
                detail: "The member has been archived successfully.",
                life: 3000,
            });
            group.value.members = group.value.members.filter((member) => member.id !== memberId);
        },
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
    });
</script>
<template>
    <div class="mx-auto w-full max-w-5xl pb-12">
        <div class="m-container mb-3">
            <PageHeading title="Group">
                <div class="ml-auto flex gap-2">
                    <Button
                        :icon="{
                            icon: 'pepicons-pencil:dots-y',
                            width: '24',
                        }"
                        class="!ring-0"
                        @click.stop="groupMenuContainer.menu.toggle($event)"
                    />
                    <Menu ref="groupMenuContainer" popup :model="groupMenuItems" />
                </div>
            </PageHeading>
        </div>
        <PageLoading v-if="!group" />
        <div v-else class="flex flex-col gap-4">
            <div class="flex flex-col gap-4 md:flex-row">
                <Card pt:body:class="h-full" pt:content:class="h-full flex items-center justify-center">
                    <div class="my-auto flex flex-col items-center justify-center p-4">
                        <Avatar
                            :label="group.icon?.url ? undefined : group.initials"
                            :image="group.icon?.url"
                            :alt="group.name"
                            class="mb-4 aspect-square w-full max-w-36 text-4xl"
                            :class="[
                                `bg-${group.color?.class}`,
                                group.color?.class === 'white' ? 'text-black' : 'text-white',
                                {
                                    'p-8': !group.icon?.url,
                                },
                            ]"
                        />
                        <div class="mb-4 flex flex-col items-center gap-2">
                            <h2 class="text-2xl font-bold">{{ group.name }}</h2>
                        </div>
                        <Chip :label="group.anonymization.name" />
                    </div>
                </Card>
                <Card>
                    <template #title>Details</template>
                    <template #content>
                        <table>
                            <tbody>
                                <tr>
                                    <td>ID</td>
                                    <td>{{ group.id }}</td>
                                </tr>
                                <tr>
                                    <td>Description</td>
                                    <td>{{ group.description }}</td>
                                </tr>
                                <tr>
                                    <td>Owner</td>
                                    <td>
                                        <RouterLink
                                            class="grow"
                                            :to="{name: 'admin.users.show', params: {user: group.user.id}}"
                                        >
                                            <div class="flex w-full items-center gap-2 py-1 pr-2">
                                                <div class="flex grow items-center gap-2">
                                                    <Avatar
                                                        :user="group.user"
                                                        class="h-10 w-10 shrink-0 !rounded-full"
                                                    />
                                                    <h3 class="grow font-normal">
                                                        {{ group.user.first_name }}
                                                        <span class="font-bold">{{ group.user.last_name }}</span>
                                                    </h3>
                                                </div>
                                            </div>
                                        </RouterLink>
                                    </td>
                                </tr>
                                <tr>
                                    <td>Created At</td>
                                    <td>
                                        {{ $date(group.created_at).format("DD/MM/YYYY HH:mm") }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </Card>
            </div>
            <!--            <Card>-->
            <!--                <template #title>Tags</template>-->
            <!--                <div v-if="group.tags.length" class="flex flex-wrap gap-2 pt-2">-->
            <!--                    <Tag v-for="tag in group.tags" :key="tag.id" :value="tag.name" />-->
            <!--                </div>-->
            <!--                <div v-else class="p-2 text-center">No tags attached.</div>-->
            <!--            </Card>-->
            <div class="flex flex-col gap-4 lg:flex-row">
                <Card>
                    <template #title>Members</template>
                    <div v-if="group.members?.length" class="flex flex-col divide-y">
                        <template v-for="member in group.members" :key="member.id">
                            <div class="relative flex w-full transition-colors duration-300 hover:bg-stone-50">
                                <RouterLink class="grow" :to="{name: 'admin.users.show', params: {user: member.id}}">
                                    <div class="flex w-full items-center gap-2 py-1 pr-2">
                                        <div class="flex grow items-center gap-2">
                                            <Avatar
                                                :image="member.profile?.avatar?.url"
                                                :label="
                                                    member.profile?.avatar?.url
                                                        ? undefined
                                                        : member.first_name[0] + member.last_name[0]
                                                "
                                                class="h-10 w-10 shrink-0 !rounded-full"
                                                :class="[
                                                    `bg-${member.profile?.color?.class}`,
                                                    member.profile?.color?.class === 'white'
                                                        ? 'text-black'
                                                        : 'text-white',
                                                ]"
                                            />
                                            <h3 class="grow font-normal">
                                                {{ member.first_name }}
                                                <span class="font-bold">{{ member.last_name }}</span>
                                            </h3>
                                        </div>
                                        <div>
                                            <Tag
                                                :value="
                                                    member.id === group.user.id
                                                        ? 'Owner'
                                                        : Str.of(member.role).capitalise().value
                                                "
                                            />
                                        </div>
                                    </div>
                                </RouterLink>
                                <div class="flex items-stretch">
                                    <Button
                                        :disabled="member.id === group.user.id"
                                        class="rounded-none !pl-1 !pr-4 hover:bg-stone-200"
                                        :icon="{
                                            icon: 'mdi:close',
                                            width: 20,
                                        }"
                                        @click="archiveMember(member.id)"
                                    />
                                </div>
                            </div>
                        </template>
                    </div>
                    <div v-else class="p-2 text-center">No members found.</div>
                </Card>
                <Card>
                    <template #title>Questions</template>
                    <div v-if="group.questions?.length" class="flex flex-col divide-y">
                        <template v-for="question in group.questions" :key="question.id">
                            <div class="relative flex w-full transition-colors duration-300 hover:bg-stone-50">
                                <RouterLink
                                    class="grow"
                                    :to="{name: 'admin.questions.show', params: {question: question.id}}"
                                >
                                    <div class="flex h-full w-full items-center gap-2 py-1 pr-2">
                                        <div class="flex grow items-center gap-2">
                                            <h3 class="grow font-bold">
                                                {{ Str.of(question.title).truncate(30) }}
                                            </h3>
                                        </div>
                                        <div class="flex w-full max-w-36 flex-col items-start gap-1 py-1">
                                            <div class="flex items-start gap-1 text-xs">
                                                <Icon icon="mdi:calendar-start-outline" width="18" />
                                                <span>
                                                    {{ $date(question.publish_from).format("DD/MM/YYYY HH:mm") }}
                                                </span>
                                            </div>
                                            <div class="flex items-start gap-1 text-xs">
                                                <Icon icon="mdi:calendar-end-outline" width="18" />
                                                <span>
                                                    {{ $date(question.publish_to).format("DD/MM/YYYY HH:mm") }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </RouterLink>
                                <!--                            <div class="flex items-stretch">-->
                                <!--                                <Button-->
                                <!--                                    class="rounded-none !pl-1 !pr-4 hover:bg-stone-200"-->
                                <!--                                    :icon="{-->
                                <!--                                    icon: 'mdi:close',-->
                                <!--                                    width: 20,-->
                                <!--                                }"-->
                                <!--                                />-->
                                <!--                            </div>-->
                            </div>
                        </template>
                    </div>
                    <div v-else class="p-2 text-center">No questions found.</div>
                </Card>
            </div>
        </div>
    </div>
</template>
<style scoped>
td {
    @apply py-1.5 pr-8 align-top;
}

tr > td:first-child {
    @apply font-bold;
}
</style>
